// BlackBox Sprite Generator — Creates all game sprites procedurally using Canvas API
// Each character is a top-down silhouette with unique glow accent

const SPRITE_SIZE = 64;  // Base sprite resolution

// Expand short hex (#fff) to full hex (#ffffff) so we can safely append alpha
function expandHex(color) {
    if (!color || color.charAt(0) !== '#') return color;
    if (color.length === 4) {
        // #rgb → #rrggbb
        return '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
    }
    return color;
}

// ============================================
// 5 CHARACTER SKIN DEFINITIONS
// ============================================
export const SKINS = [
    {
        id: 'shadow',
        name: 'Shadow',
        desc: 'Classic stealth operative',
        body: '#2a2a2a',
        outline: '#888',
        glow: '#ffffff',
        glowAlt: '#cccccc',
        eye: '#ffffff',
        trail: 'rgba(255,255,255,0.2)',
    },
    {
        id: 'phantom',
        name: 'Phantom',
        desc: 'Ghost operative',
        body: '#0a2a4a',
        outline: '#00aaff',
        glow: '#00e5ff',
        glowAlt: '#0097a7',
        eye: '#00e5ff',
        trail: 'rgba(0,229,255,0.25)',
    },
    {
        id: 'ember',
        name: 'Ember',
        desc: 'Fire specialist',
        body: '#3a1500',
        outline: '#ff6600',
        glow: '#ff6600',
        glowAlt: '#ff3300',
        eye: '#ff4400',
        trail: 'rgba(255,102,0,0.25)',
    },
    {
        id: 'specter',
        name: 'Specter',
        desc: 'Mystic assassin',
        body: '#1a0a30',
        outline: '#bb44ff',
        glow: '#bb44ff',
        glowAlt: '#8800cc',
        eye: '#cc55ff',
        trail: 'rgba(187,68,255,0.25)',
    },
    {
        id: 'wraith',
        name: 'Wraith',
        desc: 'Bio-hazard agent',
        body: '#0a2a0a',
        outline: '#44ff44',
        glow: '#44ff44',
        glowAlt: '#00cc00',
        eye: '#55ff55',
        trail: 'rgba(68,255,68,0.25)',
    },
];



// ============================================
// SPRITE CACHE — generates once, reuses forever
// ============================================
const spriteCache = new Map();

function getOrCreateCanvas(key, width, height, drawFn) {
    if (spriteCache.has(key)) return spriteCache.get(key);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    drawFn(ctx, width, height);
    spriteCache.set(key, canvas);
    return canvas;
}

// ============================================
// CHARACTER SPRITE GENERATION
// ============================================
export function generateCharacterSprite(skin, size = SPRITE_SIZE) {
    return getOrCreateCanvas(`char_${skin.id}_${size}`, size, size, (ctx, w, h) => {
        const cx = w / 2;
        const cy = h / 2;
        const bodyR = w * 0.32;
        const headR = w * 0.18;

        // Outer glow aura
        const glowGrad = ctx.createRadialGradient(cx, cy, bodyR * 0.8, cx, cy, bodyR * 2);
        glowGrad.addColorStop(0, skin.glow + '40');
        glowGrad.addColorStop(0.5, skin.glow + '15');
        glowGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = glowGrad;
        ctx.fillRect(0, 0, w, h);

        // Body circle (dark silhouette)
        ctx.beginPath();
        ctx.arc(cx, cy, bodyR, 0, Math.PI * 2);
        ctx.fillStyle = skin.body;
        ctx.fill();

        // Body outline with glow
        ctx.strokeStyle = skin.outline;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Rim light on the edge (subtle arc)
        ctx.beginPath();
        ctx.arc(cx, cy, bodyR - 1, -Math.PI * 0.3, Math.PI * 0.3);
        ctx.strokeStyle = skin.glow + '60';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Head / direction indicator (smaller circle at top)
        ctx.beginPath();
        ctx.arc(cx, cy - bodyR * 0.35, headR, 0, Math.PI * 2);
        ctx.fillStyle = skin.body;
        ctx.fill();
        ctx.strokeStyle = skin.outline;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Eyes (glowing dots)
        const eyeOffset = headR * 0.35;
        const eyeY = cy - bodyR * 0.35 - headR * 0.1;
        const eyeR = 2.5;

        // Left eye
        ctx.beginPath();
        ctx.arc(cx - eyeOffset, eyeY, eyeR, 0, Math.PI * 2);
        ctx.fillStyle = skin.eye;
        ctx.fill();
        // Eye glow
        const eyeGlow1 = ctx.createRadialGradient(cx - eyeOffset, eyeY, 0, cx - eyeOffset, eyeY, eyeR * 3);
        eyeGlow1.addColorStop(0, skin.eye + 'aa');
        eyeGlow1.addColorStop(1, 'transparent');
        ctx.fillStyle = eyeGlow1;
        ctx.fillRect(cx - eyeOffset - eyeR * 3, eyeY - eyeR * 3, eyeR * 6, eyeR * 6);

        // Right eye
        ctx.beginPath();
        ctx.arc(cx + eyeOffset, eyeY, eyeR, 0, Math.PI * 2);
        ctx.fillStyle = skin.eye;
        ctx.fill();
        const eyeGlow2 = ctx.createRadialGradient(cx + eyeOffset, eyeY, 0, cx + eyeOffset, eyeY, eyeR * 3);
        eyeGlow2.addColorStop(0, skin.eye + 'aa');
        eyeGlow2.addColorStop(1, 'transparent');
        ctx.fillStyle = eyeGlow2;
        ctx.fillRect(cx + eyeOffset - eyeR * 3, eyeY - eyeR * 3, eyeR * 6, eyeR * 6);

        // Shoulder marks (tiny accent lines)
        ctx.beginPath();
        ctx.moveTo(cx - bodyR * 0.5, cy + bodyR * 0.2);
        ctx.lineTo(cx - bodyR * 0.7, cy + bodyR * 0.35);
        ctx.strokeStyle = skin.glow + '80';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(cx + bodyR * 0.5, cy + bodyR * 0.2);
        ctx.lineTo(cx + bodyR * 0.7, cy + bodyR * 0.35);
        ctx.stroke();
    });
}

// ============================================
// WALL TEXTURE GENERATION
// ============================================
export function generateWallTexture(width = 64, height = 64) {
    return getOrCreateCanvas('wall_tex', width, height, (ctx, w, h) => {
        // Dark industrial metal base
        ctx.fillStyle = '#1e2328';
        ctx.fillRect(0, 0, w, h);

        // Metal panel lines
        ctx.strokeStyle = '#2a3038';
        ctx.lineWidth = 1;

        // Horizontal lines
        for (let y = 0; y < h; y += 16) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(w, y);
            ctx.stroke();
        }
        // Vertical lines
        for (let x = 0; x < w; x += 16) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, h);
            ctx.stroke();
        }

        // Rivet dots at intersections
        ctx.fillStyle = '#3a4048';
        for (let x = 0; x < w; x += 16) {
            for (let y = 0; y < h; y += 16) {
                ctx.beginPath();
                ctx.arc(x, y, 1.5, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // Subtle noise/grain
        for (let i = 0; i < 100; i++) {
            const rx = Math.random() * w;
            const ry = Math.random() * h;
            ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
            ctx.fillRect(rx, ry, 1, 1);
        }
    });
}

// ============================================
// FLOOR TEXTURE GENERATION
// ============================================
export function generateFloorTexture(width = 128, height = 128) {
    return getOrCreateCanvas('floor_tex', width, height, (ctx, w, h) => {
        // Dark concrete base
        ctx.fillStyle = '#0a0c0e';
        ctx.fillRect(0, 0, w, h);

        // Concrete texture (subtle variation)
        for (let i = 0; i < 500; i++) {
            const rx = Math.random() * w;
            const ry = Math.random() * h;
            const brightness = Math.random() * 0.04;
            ctx.fillStyle = `rgba(255,255,255,${brightness})`;
            ctx.fillRect(rx, ry, 1 + Math.random() * 2, 1 + Math.random() * 2);
        }

        // Grid lines (faint tile seams)
        ctx.strokeStyle = 'rgba(255,255,255,0.02)';
        ctx.lineWidth = 1;
        for (let x = 0; x < w; x += 32) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, h);
            ctx.stroke();
        }
        for (let y = 0; y < h; y += 32) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(w, y);
            ctx.stroke();
        }

        // Occasional stain/mark
        for (let i = 0; i < 3; i++) {
            const sx = Math.random() * w;
            const sy = Math.random() * h;
            const stainGrad = ctx.createRadialGradient(sx, sy, 0, sx, sy, 8 + Math.random() * 12);
            stainGrad.addColorStop(0, 'rgba(20,15,10,0.3)');
            stainGrad.addColorStop(1, 'transparent');
            ctx.fillStyle = stainGrad;
            ctx.fillRect(sx - 20, sy - 20, 40, 40);
        }
    });
}

// ============================================
// BULLET SPRITE
// ============================================
export function generateBulletSprite(color = '#fff', size = 12) {
    const key = `bullet_${color}_${size}`;
    return getOrCreateCanvas(key, size, size, (ctx, w, h) => {
        const cx = w / 2;
        const cy = h / 2;
        const r = w * 0.25;

        // Outer glow
        const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, w / 2);
        const safeColor = expandHex(color);
        glow.addColorStop(0, safeColor);
        glow.addColorStop(0.3, safeColor + 'aa');
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, w, h);

        // Core
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();
    });
}

// ============================================
// MUZZLE FLASH SPRITE
// ============================================
export function generateMuzzleFlash(color = '#fff', size = 24) {
    const key = `muzzle_${color}_${size}`;
    return getOrCreateCanvas(key, size, size, (ctx, w, h) => {
        const cx = w / 2;
        const cy = h / 2;

        // Flash glow
        const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, w / 2);
        const safeColor = expandHex(color);
        glow.addColorStop(0, '#ffffff');
        glow.addColorStop(0.2, safeColor + 'cc');
        glow.addColorStop(0.5, safeColor + '44');
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, w, h);

        // Star rays
        ctx.strokeStyle = safeColor + '80';
        ctx.lineWidth = 1;
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI * 2) / 6;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.cos(angle) * w * 0.4, cy + Math.sin(angle) * h * 0.4);
            ctx.stroke();
        }
    });
}

// ============================================
// CROSSHAIR SPRITE
// ============================================
export function generateCrosshair(color = '#fff', size = 32) {
    return getOrCreateCanvas(`crosshair_${color}`, size, size, (ctx, w, h) => {
        const cx = w / 2;
        const cy = h / 2;

        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = 0.8;

        // Outer circle
        ctx.beginPath();
        ctx.arc(cx, cy, w * 0.35, 0, Math.PI * 2);
        ctx.stroke();

        // Cross lines (with gap in center)
        const gap = 4;
        const len = w * 0.35;
        // Top
        ctx.beginPath(); ctx.moveTo(cx, cy - gap); ctx.lineTo(cx, cy - len); ctx.stroke();
        // Bottom
        ctx.beginPath(); ctx.moveTo(cx, cy + gap); ctx.lineTo(cx, cy + len); ctx.stroke();
        // Left
        ctx.beginPath(); ctx.moveTo(cx - gap, cy); ctx.lineTo(cx - len, cy); ctx.stroke();
        // Right
        ctx.beginPath(); ctx.moveTo(cx + gap, cy); ctx.lineTo(cx + len, cy); ctx.stroke();

        // Center dot
        ctx.beginPath();
        ctx.arc(cx, cy, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        ctx.globalAlpha = 1;
    });
}

// ============================================
// SKIN SELECTOR PREVIEW (larger, for lobby UI)
// ============================================
export function generateSkinPreview(skin, size = 120) {
    return getOrCreateCanvas(`preview_${skin.id}_${size}`, size, size, (ctx, w, h) => {
        const cx = w / 2;
        const cy = h / 2;

        // Background circle
        const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, w / 2);
        bgGrad.addColorStop(0, skin.glow + '20');
        bgGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, w, h);

        // Draw character larger
        const charCanvas = generateCharacterSprite(skin, size);
        ctx.drawImage(charCanvas, 0, 0);
    });
}
